// built-from: /chat-component v3 impl:chat-component role:implementation
// Realtime voice mode. Owns the WebRTC peer/data channels, audio
// element, transcript buffers, and the per-turn dispatch queue. The
// dc.onmessage handler routes finalized transcripts depending on mode.
// Both discovery and watch feed /chat2 (the flow brain), queued so
// concurrent transcripts can't race the read-modify-write on the
// session blob. Discovery speaks the reply via speakViaRealtime;
// watch listens silently (no TTS).
//
// Voice and text dispatch (handleTextMessage) are intentionally NOT
// converged: voice ignores nextQuestion in the chat history (Realtime
// is already speaking it), uses the queue ref, and never renders
// agent text into history early. Forcing them to share would either
// bloat the call site with mode flags or produce a worse abstraction.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from 'react';
import { stripDsnFromText } from '../utils/summaryParser';
import { streamChat2, type DoneModel } from '../utils/chat2Stream';
import type { NewModel } from '../utils/flowModel';
import type { ConversationMessage, ConversationMode } from './sessionTypes';

export interface UseVoiceOptions {
  sessionIdRef: MutableRefObject<string | null>;
  modeRef: MutableRefObject<ConversationMode>;
  memorySummaryRef: MutableRefObject<string>;
  conversationHistory: ConversationMessage[];
  setConversationHistory: Dispatch<SetStateAction<ConversationMessage[]>>;
  setNewModel: Dispatch<SetStateAction<NewModel>>;
  // The shared turn-result applier from useSession. Voice's onDone hands
  // it the whole done frame so phase/spine/kind build in voice exactly as
  // they do on the text path, and newModel stays clean (the single shared
  // writer — 003-narrate-voice-feedback, FR-001..FR-003).
  applyTurnResult: (doneModel: DoneModel) => void;
  // Snapshot reads at call time so the realtime opening line can
  // resume mid-discovery without going stale across re-renders.
  newModelFlowText: string;
  newModelNextQuestion: string;

  // ── 052 voice on the one chat (Slice 1) — ADDITIVE, OPTIONAL ─────────────
  // A screen parameterises the ONE voice hook the same way it parameterises the
  // text path (049): a spoken turn feeds the SAME engine, screen-aware, with the
  // screen's posture. The discovery caller (useSession) passes NONE of these, so
  // its behaviour is byte-identical to today. There is NO per-screen branch in the
  // hook — screen identity rides the opaque `context` / `screenContext` (the
  // generality guard scans this file).
  //
  // The chat2 context discriminator threaded as streamChat2's arg 6 (e.g.
  // 'vision'); absent → discovery (no discriminator).
  context?: string;
  // The live on-screen snapshot ref, read AT CALL TIME and threaded as arg 7 (so a
  // changed snapshot flows into the next spoken turn — 049 SC-002).
  screenContextRef?: MutableRefObject<unknown>;
  // Run the SAME post-turn sink the text path runs on a spoken onDone (e.g. surface
  // candidates + refresh the engagement — 049 FR-004/FR-017).
  onDoneEnvelope?: (model: DoneModel) => void;
  // The realtime mode posted when minting the session; absent → modeRef.current
  // (today). Vision passes 'watch' (the existing silent-observer posture — no
  // secret-mint change in this slice).
  voiceModeForSession?: string;
  // The auto-opening line spoken on dc.onopen. `null` → speak NO opener (vision);
  // absent (undefined) → the existing discovery opener logic (resume/greeting).
  openingLine?: string | null;
  // Whether to speak the engine's reply via TTS. Absent → today's posture
  // (`!isWatchMode`); `false` → never speak the reply (still transcribed, no data
  // loss — FR-006/FR-009).
  speakReplies?: boolean;
}

export type CallStatus = 'Disconnected' | 'Connecting...' | 'On Call' | 'Error';

export interface UseVoiceReturn {
  callStatus: CallStatus;
  isVoiceWaiting: boolean;
  startVoiceCall: () => Promise<void>;
  endVoiceCall: () => void;
}

export function useVoice(opts: UseVoiceOptions): UseVoiceReturn {
  const {
    sessionIdRef,
    modeRef,
    memorySummaryRef,
    conversationHistory,
    setConversationHistory,
    applyTurnResult,
    newModelFlowText,
    newModelNextQuestion,
    // 052 (additive, optional — discovery passes none).
    context: voiceContext,
    screenContextRef,
    onDoneEnvelope,
    voiceModeForSession,
    openingLine,
    speakReplies: speakRepliesOpt,
  } = opts;

  const [callStatus, setCallStatus] = useState<CallStatus>('Disconnected');
  const [isVoiceWaiting, setIsVoiceWaiting] = useState(false);

  // ── Refs ──────────────────────────────────────────────────────
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const agentBufferRef = useRef('');
  const userBufferRef = useRef('');
  const voiceHistoryRef = useRef<ConversationMessage[]>([]);
  // Voice sends each finalized transcript to /chat2. Realtime can fire
  // "final transcript" events back-to-back (fast speakers, partial re-
  // segmentation), and /chat2's load-mutate-save on a session isn't
  // atomic — two concurrent calls would clobber each other's flow writes.
  // Chaining on this promise ensures each turn waits for the previous
  // /chat2 to persist before the next one starts.
  const voiceChat2QueueRef = useRef<Promise<void>>(Promise.resolve());
  // True while the assistant is generating/speaking a response. Used as an
  // echo guard: the AI's own voice can leak into the mic and get transcribed
  // as a bogus "user turn", which would cut the reply off mid-sentence. While
  // this is true we ignore input transcription. Cleared on `response.done`.
  const aiSpeakingRef = useRef(false);

  // Lazily create a hidden audio element for Realtime playback.
  const getAudioElement = useCallback(() => {
    if (!audioElRef.current) {
      const el = document.createElement('audio');
      el.autoplay = true;
      el.style.display = 'none';
      document.body.appendChild(el);
      audioElRef.current = el;
    }
    return audioElRef.current;
  }, []);

  // Cleanup on unmount: close peer/data channels, stop audio tracks.
  useEffect(() => {
    return () => {
      if (pcRef.current) {
        pcRef.current.close();
      }
      if (dcRef.current) {
        dcRef.current.close();
      }
      if (audioElRef.current?.srcObject) {
        (audioElRef.current.srcObject as MediaStream)
          .getTracks()
          .forEach((track) => track.stop());
      }
    };
  }, []);

  const startVoiceCall = useCallback(async () => {
    if (callStatus === 'On Call' || callStatus === 'Connecting...') return;

    const isWatchMode = modeRef.current === 'watch';
    const hasExistingFlow = (newModelFlowText || '').trim().length > 0;

    // Snapshot current conversation for voice tracking.
    voiceHistoryRef.current = [...conversationHistory];
    setCallStatus('Connecting...');

    try {
      // Acquire the mic BEFORE minting the ephemeral token. getUserMedia can
      // block on a slow permission prompt; doing it first means the short-lived
      // client secret isn't already counting down (and possibly expired) by the
      // time we use it for the SDP exchange.
      const stream = await navigator.mediaDevices.getUserMedia({
        // Echo cancellation + noise suppression reduce the assistant's own
        // playback bleeding back into the mic (compounds the aiSpeaking guard).
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });

      // Server derives flow state from the session itself — we pass sessionId
      // so it can load the current newModel. memorySummary/hasConversation
      // are kept for backward compat with legacy callers/sessions.
      const sessionResponse = await fetch('/api/realtime-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionIdRef.current,
          // 052: the screen's posture (vision passes 'watch' — the existing
          // silent-observer mode, no secret-mint change); absent → modeRef.current.
          mode: voiceModeForSession ?? modeRef.current,
          memorySummary: memorySummaryRef.current,
          hasConversation: conversationHistory.length > 0,
        }),
      });

      const sessionData = await sessionResponse.json();

      if (!sessionResponse.ok) {
        throw new Error(sessionData.error || 'Failed to create realtime session');
      }

      // GA /v1/realtime/client_secrets returns the ephemeral token at the
      // top-level `value`; keep the legacy `client_secret.value` as a fallback.
      const clientSecret = sessionData?.value ?? sessionData?.client_secret?.value;
      if (!clientSecret) {
        throw new Error('Missing client secret from realtime session');
      }

      const pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
      });
      pcRef.current = pc;

      pc.onconnectionstatechange = () => {
        const st = pc.connectionState;
        if (st === 'failed' || st === 'disconnected' || st === 'closed') {
          setCallStatus('Error');
          setIsVoiceWaiting(false);
        }
      };

      // Attach the mic tracks acquired before the token was minted.
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      const audioEl = getAudioElement();
      pc.ontrack = (event) => {
        audioEl.srcObject = event.streams[0];
      };

      const dc = pc.createDataChannel('oai-events');
      dcRef.current = dc;

      dc.onclose = () => setCallStatus((s) => (s === 'On Call' ? 'Error' : s));
      dc.onerror = (e) => {
        console.error('Voice data channel error:', e);
        setCallStatus('Error');
        setIsVoiceWaiting(false);
      };

      // Hand text to the Realtime session to speak.
      //
      // We use response.create with an inline `input` override so the
      // response is generated from ONLY the item we pass (not conversation
      // history, not the user's last speech), and no auto-response fires.
      //
      // Previous attempt put the raw text as role:user content — the model
      // then responded TO it (e.g. if we sent "Hi I'm ArcPilot..." as a user
      // message, Realtime replied "Hi ArcPilot, nice to meet you!"). The
      // fix is to frame it as an explicit user directive — "Say this
      // exactly: ..." — so the role semantics line up and the assistant
      // response IS the text we want spoken.
      const speakViaRealtime = (text: string) => {
        const trimmed = text ? text.trim() : '';
        if (!trimmed) return;
        if (dc.readyState !== 'open') return;
        // Mark the assistant as speaking before the audio starts so the echo
        // guard suppresses mic input for the duration of this reply.
        aiSpeakingRef.current = true;
        dc.send(
          JSON.stringify({
            type: 'response.create',
            response: {
              // GA renamed `modalities` → `output_modalities`, and audio+text
              // can't be requested together — ['audio'] yields speech plus a
              // transcript (captured via response.audio_transcript.* events).
              output_modalities: ['audio'],
              instructions:
                'The user is asking you to repeat a sentence. Your entire response must be that sentence, spoken verbatim, with no preamble, acknowledgement, or added words. Do not greet. Do not introduce yourself. Do not comment. Just speak the exact text the user provided. Deliver it calmly and evenly, at a measured professional pace, with natural neutral intonation — never theatrical, breathless, or sing-song.',
              input: [
                {
                  type: 'message',
                  role: 'user',
                  content: [
                    {
                      type: 'input_text',
                      text: `Please say this exactly as written, with no additions or changes:\n\n${trimmed}`,
                    },
                  ],
                },
              ],
            },
          }),
        );
      };

      dc.onopen = () => {
        setCallStatus('On Call');
        if (isWatchMode) return;

        // 052: a screen may explicitly suppress the auto-opener (openingLine:null
        // — vision listens without an opener) or supply its own line. When the
        // option is ABSENT (discovery), the existing resume/greeting logic runs
        // unchanged (byte-identical).
        if (openingLine === null) return;
        if (typeof openingLine === 'string') {
          speakViaRealtime(openingLine);
          return;
        }

        // Opening line: if the session already has a pending next question
        // (resumed mid-discovery), speak that. Otherwise a canned greeting.
        // Either way, Realtime never generates this text itself — it only
        // reads what we hand it.
        const pendingNq = (newModelNextQuestion || '').trim();
        if (hasExistingFlow && pendingNq) {
          speakViaRealtime(pendingNq);
        } else {
          speakViaRealtime(
            "Walk me through this process from start to finish, in your own words. Don't worry about getting the order perfect or leaving things out — I'll keep track and we'll tidy it up together afterwards.",
          );
        }
      };

      dc.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          const eventType = payload?.type || '';

          if (eventType === 'error') {
            console.error('Realtime error:', payload.error);
            setCallStatus('Error');
            setIsVoiceWaiting(false);
            aiSpeakingRef.current = false;
            return;
          }

          // ── Assistant audio transcript (GA: response.output_audio_transcript.*;
          //    Beta fallback: response.audio_transcript.*). Stream into the UI
          //    live so the reply builds up on screen as it's spoken.
          if (
            (eventType === 'response.output_audio_transcript.delta' ||
              eventType === 'response.audio_transcript.delta') &&
            payload.delta
          ) {
            aiSpeakingRef.current = true;
            agentBufferRef.current += payload.delta;
            setConversationHistory([
              ...voiceHistoryRef.current,
              { type: 'agent', text: stripDsnFromText(agentBufferRef.current), timestamp: new Date().toLocaleTimeString() },
            ]);
            return;
          }

          if (
            eventType === 'response.output_audio_transcript.done' ||
            eventType === 'response.audio_transcript.done'
          ) {
            const finalText = payload.transcript || payload.text || agentBufferRef.current;
            if (finalText?.trim()) {
              const agentMsg: ConversationMessage = {
                type: 'agent',
                text: stripDsnFromText(finalText.trim()),
                timestamp: new Date().toLocaleTimeString(),
              };
              voiceHistoryRef.current = [...voiceHistoryRef.current, agentMsg];
              setConversationHistory([...voiceHistoryRef.current]);
            }
            agentBufferRef.current = '';
            return;
          }

          // Whole response finished (audio generation done) — the assistant has
          // stopped speaking. Release the echo guard so the next user turn is
          // accepted, and drop the waiting state.
          if (eventType === 'response.done') {
            aiSpeakingRef.current = false;
            setIsVoiceWaiting(false);
            return;
          }

          // ── User input transcription (GA: conversation.item.input_audio_transcription.delta/.completed).
          if (eventType.includes('input_audio_transcription')) {
            // Echo guard: while the assistant is speaking, its own voice can
            // leak into the mic and be transcribed. Ignore input transcription
            // during AI speech so we neither render echo as user text nor fire
            // a turn that cuts the reply off. (Watch never sets aiSpeaking.)
            if (aiSpeakingRef.current) return;

            const isFinal = eventType.endsWith('.completed') || eventType.endsWith('.done');
            if (!isFinal) {
              // Partial delta — accumulate and show live.
              userBufferRef.current += (payload.delta || payload.transcript || '');
              if (userBufferRef.current.trim()) {
                setConversationHistory([
                  ...voiceHistoryRef.current,
                  { type: 'user', text: userBufferRef.current, timestamp: new Date().toLocaleTimeString() },
                ]);
              }
              return;
            }

            // Final — prefer the server's full transcript over the accumulated deltas.
            const finalUserText = (payload.transcript || userBufferRef.current || '').trim();
            userBufferRef.current = '';
            if (!finalUserText) return;

            const userMsg: ConversationMessage = {
              type: 'user',
              text: finalUserText,
              timestamp: new Date().toLocaleTimeString(),
            };
            voiceHistoryRef.current = [...voiceHistoryRef.current, userMsg];
            setConversationHistory([...voiceHistoryRef.current]);
            setIsVoiceWaiting(true);

            const sid = sessionIdRef.current;
            if (!sid) return;

            // Both discovery and watch feed /chat2 (the flow brain). Queue each
            // turn behind the previous so concurrent transcripts can't race the
            // server-side read-modify-write. Discovery speaks the reply; watch
            // listens silently. 052: a screen may pin the speak posture explicitly
            // (vision passes speakReplies:false); absent → today's `!isWatchMode`.
            const speakReplies = speakRepliesOpt ?? !isWatchMode;
            voiceChat2QueueRef.current = voiceChat2QueueRef.current
              .catch(() => { /* swallow prior failure so this turn still runs */ })
              .then(async () => {
                // The `spoken` latch keeps a reply to exactly one utterance
                // across the early + onDone points (no double-speak, SC-004).
                let spoken = false;
                try {
                  await streamChat2(sid, finalUserText, {
                    // ── Quiet-cadence gate, early point (FR-005/FR-008) ──
                    // The early `nextQuestion` event carries `kind` for
                    // nudge/offer but NEVER `phase`. To make a nudge
                    // structurally un-speakable early, speak here ONLY for an
                    // explicit `offer`; everything else (nudge, narrate-
                    // question with no kind, or any non-offer) defers to the
                    // authoritative onDone gate below. (Voice has no early-
                    // render benefit the way text does, so deferring costs
                    // only stream-tail latency.)
                    onNextQuestion: (text, kind) => {
                      if (!speakReplies || spoken) return;
                      if (kind !== 'offer') return;
                      speakViaRealtime(text);
                      spoken = true;
                    },
                    // ── Quiet-cadence gate, authoritative point ──────────
                    // onDone has BOTH phase and kind. Apply the frame through
                    // the shared applier first (P1: builds spine/phase/kind,
                    // strips newModel — FR-001..FR-003), then decide whether
                    // to speak from the frame's own phase/kind.
                    onDone: (agentResponse, newModelValue) => {
                      // Single shared writer (P1): builds spine/phase/kind in
                      // voice and strips newModel, identical to the text path.
                      // Optional-chained so the voice unit harness can drive
                      // the speak gate without wiring the applier; production
                      // always supplies it (useSession).
                      applyTurnResult?.(newModelValue);
                      // 052: run the screen's post-turn sink (e.g. surface vision
                      // candidates + refresh the engagement) so a SPOKEN turn binds
                      // chat → state → UI exactly like the typed turn (049
                      // FR-004/FR-017). Absent for discovery.
                      onDoneEnvelope?.(newModelValue);

                      const phase = newModelValue.phase;
                      const kind = newModelValue.kind;
                      // narrate → speak ONLY the offer (opener is handled on
                      // dc.onopen, outside this gate). refine/absent → speak
                      // as today (FR-007, refine voice not regressed).
                      const speakThisTurn = phase === 'narrate'
                        ? kind === 'offer'
                        : true;

                      // A screen that explicitly chose silence (speakReplies:false
                      // — vision) still surfaces the reply in the transcript (the
                      // only "heard" channel when silent). Discovery's existing
                      // watch posture (speakReplies defaulted to false, option
                      // ABSENT) keeps its current behaviour — no onDone append — so
                      // discovery stays byte-identical.
                      const appendWhenSilent = speakReplies || speakRepliesOpt === false;

                      if (speakReplies && !spoken && agentResponse && speakThisTurn) {
                        speakViaRealtime(agentResponse);
                        spoken = true;
                      } else if (appendWhenSilent && !spoken && agentResponse) {
                        // The reply is NOT spoken this turn — either a suppressed
                        // narrate reply (e.g. a nudge, discovery) or a silent
                        // posture (speakReplies:false, vision). Silence is NOT data
                        // loss: append the agent line to the transcript via the same
                        // voiceHistoryRef → setConversationHistory mechanism the
                        // spoken-transcript path uses (FR-006/FR-009).
                        const agentMsg: ConversationMessage = {
                          type: 'agent',
                          text: stripDsnFromText(agentResponse),
                          timestamp: new Date().toLocaleTimeString(),
                        };
                        voiceHistoryRef.current = [...voiceHistoryRef.current, agentMsg];
                        setConversationHistory([...voiceHistoryRef.current]);
                      }
                    },
                  },
                  // 052: thread the screen-aware args the text path threads (049
                  // FR-001) so a SPOKEN turn reaches the screen's facilitator, not
                  // the screen-blind engine. files/signal stay undefined (voice has
                  // no attachments here); context = arg 6, screenContext = arg 7,
                  // read AT CALL TIME (a changed ref flows into the next turn).
                  // Absent for discovery → byte-identical (call[5]/call[6]
                  // undefined).
                  undefined,
                  undefined,
                  voiceContext,
                  screenContextRef?.current,
                  undefined,
                  {
                    inputId: String(payload.item_id || payload.item?.id || `voice-${Date.now()}`),
                    modality: 'voice',
                    sourceRef: String(payload.item_id || payload.item?.id || 'realtime-transcript'),
                  },
                  );
                } catch (err) {
                  console.error('Voice /chat2 sync error:', err);
                } finally {
                  // Discovery clears the waiting state on `response.done` (when
                  // the AI actually finishes speaking). Clear here for watch, or
                  // when discovery produced no spoken reply (error / empty /
                  // suppressed nudge).
                  if (!speakReplies || !spoken) setIsVoiceWaiting(false);
                }
              });
            return;
          }
        } catch (error) {
          console.warn('Realtime message parsing error:', error);
        }
      };

      const offer = await pc.createOffer({ offerToReceiveAudio: true });
      await pc.setLocalDescription(offer);

      // GA WebRTC SDP exchange endpoint. The model/voice/VAD are bound to the
      // ephemeral token's session, so no ?model= query is needed here. (The
      // deprecated /v1/realtime?model= path is gone.)
      const realtimeResponse = await fetch(
        'https://api.openai.com/v1/realtime/calls',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${clientSecret}`,
            'Content-Type': 'application/sdp',
          },
          body: offer.sdp,
        },
      );

      if (realtimeResponse.status === 401) {
        throw new Error('Voice token expired before connecting — please try again.');
      }
      if (!realtimeResponse.ok) {
        throw new Error('Failed to establish realtime connection');
      }

      const answerSdp = await realtimeResponse.text();
      await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
    } catch (error) {
      console.error('Voice call error:', error);
      setCallStatus('Error');
    }
  }, [
    callStatus,
    conversationHistory,
    getAudioElement,
    memorySummaryRef,
    modeRef,
    newModelFlowText,
    newModelNextQuestion,
    sessionIdRef,
    setConversationHistory,
    applyTurnResult,
    // 052 additive options (read at call time; included for exhaustive-deps).
    voiceContext,
    screenContextRef,
    onDoneEnvelope,
    voiceModeForSession,
    openingLine,
    speakRepliesOpt,
  ]);

  const endVoiceCall = useCallback(() => {
    // Detach the connection-state handlers before closing so a deliberate
    // hangup doesn't trip the onclose/onconnectionstatechange('closed') paths
    // that set 'Error' — we want the final state to be 'Disconnected'.
    if (dcRef.current) {
      dcRef.current.onclose = null;
      dcRef.current.onerror = null;
      dcRef.current.close();
      dcRef.current = null;
    }

    if (pcRef.current) {
      pcRef.current.onconnectionstatechange = null;
      pcRef.current.getSenders().forEach((sender) => {
        if (sender.track) {
          sender.track.stop();
        }
      });
      pcRef.current.close();
      pcRef.current = null;
    }

    if (audioElRef.current?.srcObject) {
      (audioElRef.current.srcObject as MediaStream)
        .getTracks()
        .forEach((track) => track.stop());
      audioElRef.current.srcObject = null;
    }

    setCallStatus('Disconnected');
    setIsVoiceWaiting(false);
    aiSpeakingRef.current = false;
  }, []);

  return { callStatus, isVoiceWaiting, startVoiceCall, endVoiceCall };
}
